import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { RecorrenciasService, fmtMoeda, fmtData } from '../services';

export default function Recorrencias() {
  const [recorrencias, setRecorrencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [desativando, setDesativando] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = mostrarInativas ? undefined : { ativo: 1 };
    RecorrenciasService.listar(params)
      .then(setRecorrencias)
      .catch(() => setRecorrencias([]))
      .finally(() => setLoading(false));
  }, [mostrarInativas]);

  useEffect(load, [load]);

  const handleDesativar = async (r) => {
    const aviso = r.parcelas_futuras > 0
      ? `Desativar "${r.descricao}"?\n\nIsso excluirá ${r.parcelas_futuras} parcela(s) pendente(s) futura(s).`
      : `Desativar "${r.descricao}"?`;
    if (!window.confirm(aviso)) return;
    setDesativando(r.id);
    try {
      const res = await RecorrenciasService.desativar(r.id);
      if (res.excluidas > 0) alert(`Recorrência desativada. ${res.excluidas} parcela(s) futura(s) excluída(s).`);
      load();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao desativar');
    } finally {
      setDesativando(null);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Excluir a recorrência "${r.descricao}"?\n\nAs transações já geradas NÃO serão excluídas.`)) return;
    try { await RecorrenciasService.excluir(r.id); load(); }
    catch (err) { alert(err.response?.data?.erro || 'Erro'); }
  };

  const ativas = recorrencias.filter(r => r.ativo);

  return (
    <Layout title="Recorrências">

      {/* Banner informativo */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 14,
        background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)',
        border: '1px solid #a78bfa', borderRadius: 12,
        padding: '14px 18px', marginBottom: 20,
      }}>
        <span style={{ fontSize: 22, flexShrink: 0 }}>🔁</span>
        <div>
          <div style={{ fontWeight: 700, color: '#6d28d9', fontSize: 14, marginBottom: 3 }}>
            Como criar uma recorrência
          </div>
          <div style={{ fontSize: 13, color: '#7c3aed', lineHeight: 1.6 }}>
            No formulário de <strong>Nova Transação</strong>, selecione uma conta bancária e ative{' '}
            <strong>"Repetir mensalmente"</strong>. Escolha 3, 6, 12 ou 24 meses — as parcelas futuras
            ficam como <em>Pendentes</em> e aparecem automaticamente em cada mês.
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex-between mb-3">
        <span style={{ fontSize: 13, color: 'var(--gray-500)', fontWeight: 500 }}>
          {ativas.length} recorrência{ativas.length !== 1 ? 's' : ''} ativa{ativas.length !== 1 ? 's' : ''}
        </span>
        <div
          onClick={() => setMostrarInativas(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            userSelect: 'none', padding: '6px 14px', borderRadius: 9,
            background: mostrarInativas ? '#f5f3ff' : 'var(--gray-100)',
            border: `1.5px solid ${mostrarInativas ? '#a78bfa' : 'var(--gray-200)'}`,
            fontSize: 12, fontWeight: 600,
            color: mostrarInativas ? '#6d28d9' : 'var(--gray-500)',
            transition: 'all .15s',
          }}>
          <div style={{
            width: 28, height: 16, borderRadius: 8,
            background: mostrarInativas ? '#7c3aed' : 'var(--gray-300)',
            position: 'relative', transition: 'background .2s', flexShrink: 0,
          }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%', background: 'white',
              position: 'absolute', top: 2, left: mostrarInativas ? 14 : 2,
              transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
            }} />
          </div>
          Ver inativas
        </div>
      </div>

      {loading ? (
        <div className="loading-wrap"><div className="spinner" /></div>
      ) : recorrencias.length === 0 ? (
        <div className="empty-state card">
          <div className="empty-icon">🔁</div>
          <p style={{ fontWeight: 600 }}>
            {mostrarInativas ? 'Nenhuma recorrência cadastrada.' : 'Nenhuma recorrência ativa.'}
          </p>
          <p style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 4 }}>
            Crie uma transação recorrente pelo formulário de <strong>Nova Transação</strong>.
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th>Conta</th>
                  <th>Período</th>
                  <th>Parcelas</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recorrencias.map(r => (
                  <tr key={r.id} style={{ opacity: r.ativo ? 1 : 0.5 }}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.descricao}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                        <span className={`badge ${r.tipo === 'receita' ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: 10 }}>
                          {r.tipo}
                        </span>
                        {r.categoria_icone && (
                          <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                            {r.categoria_icone} {r.categoria_nome}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>{r.conta_nome || r.cartao_nome || '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      <div style={{ fontWeight: 500 }}>{fmtData(r.data_inicio)}</div>
                      {r.data_fim && (
                        <div style={{ color: 'var(--gray-400)' }}>até {fmtData(r.data_fim)}</div>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {r.total_parcelas > 0 ? (
                        <div>
                          <span style={{ fontWeight: 600 }}>{r.total_parcelas}</span>
                          <span style={{ color: 'var(--gray-400)' }}> total</span>
                          {r.parcelas_futuras > 0 && (
                            <div style={{ color: '#d97706', fontWeight: 600, marginTop: 2 }}>
                              {r.parcelas_futuras} pendente{r.parcelas_futuras !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--gray-300)' }}>—</span>
                      )}
                    </td>
                    <td className={r.tipo === 'receita' ? 'value-positive' : 'value-negative'} style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {r.tipo === 'receita' ? '+' : '-'}{fmtMoeda(r.valor)}
                    </td>
                    <td>
                      <span className={`badge ${r.ativo ? 'badge-success' : 'badge-gray'}`}>
                        {r.ativo ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td>
                      <div className="flex-gap gap-sm">
                        {r.ativo && (
                          <button
                            className="btn-action"
                            title="Desativar"
                            disabled={desativando === r.id}
                            onClick={() => handleDesativar(r)}
                            style={{
                              background: '#fff7ed', border: '1.5px solid #fed7aa',
                              color: '#d97706', borderRadius: 8, width: 32, height: 32,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', transition: 'all .15s', padding: 0,
                            }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                            </svg>
                          </button>
                        )}
                        <button className="btn-action btn-action-delete" title="Excluir" onClick={() => handleDelete(r)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}
